/**
 * Handler capacity calculation utilities
 * Single source of truth for calculating handler open slots
 */

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

const normalizeId = (value) => String(value || '')
  .trim()
  .replace(/[{}]/g, '')
  .toLowerCase();

const unwrapRequest = (request) => {
  if (!request || typeof request !== 'object') return {};

  return firstDefined(
    request?.handlerRequestData,
    request?.HandlerRequestData,
    request?.requestData,
    request?.RequestData,
    request
  ) || request;
};

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const isAssignedRequest = (request) => {
  const source = unwrapRequest(request);
  const status = normalizeStatus(firstDefined(source?.status, source?.Status, source?.requestStatus, source?.RequestStatus));
  return status === 'approved' || status === 'assigned' || status === 'accepted' || status === 'complete' || status === 'completed';
};

const getRequestBirdCount = (request) => {
  const source = unwrapRequest(request);
  const birdDetails = firstDefined(source?.birdDetails, source?.BirdDetails, source?.birds, source?.Birds);

  return toNumber(
    source?.numberOfBirds
    ?? source?.birdsRequested
    ?? source?.NumberOfBirds
    ?? source?.BirdsRequested
    ?? source?.birdCount
    ?? source?.BirdCount
    ?? source?.assignedBirds
    ?? source?.AssignedBirds
    ?? (Array.isArray(birdDetails) ? birdDetails.length : undefined)
    ?? 0
  );
};

const getRequestHandlerId = (request) => {
  const source = unwrapRequest(request);
  return normalizeId(firstDefined(source?.handlerId, source?.HandlerId, source?.handlerID));
};

const getRequestBreederId = (request) => {
  const source = unwrapRequest(request);
  return normalizeId(firstDefined(source?.breederId, source?.BreederId, source?.breederID));
};

const getRequestKey = (request, index = 0) => {
  const source = unwrapRequest(request);
  return String(firstDefined(
    source?.id,
    source?.requestId,
    source?.RequestId,
    `${getRequestBreederId(source)}-${getRequestHandlerId(source)}-${firstDefined(source?.requestDate, source?.RequestDate, 'date')}-${getRequestBirdCount(source)}-${index}`
  ));
};

const uniqueRequests = (requests = []) => {
  const seen = new Set();
  return requests.filter((request, index) => {
    const key = getRequestKey(request, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getHandlerTotalCapacity = (handler) => {
  const info = handler?.handlerInfo || {};
  return toNumber(
    info?.totalBirds
    ?? info?.totalCapacity
    ?? info?.birdCapacity
    ?? info?.capacity
    ?? handler?.totalBirds
    ?? handler?.totalCapacity
    ?? handler?.birdCapacity
    ?? handler?.capacity
    ?? 0
  );
};

const getHandlerReservedSlots = (handler) => {
  const info = handler?.handlerInfo || {};
  return toNumber(
    info?.reserved
    ?? info?.reservedSlots
    ?? handler?.reserved
    ?? handler?.reservedSlots
    ?? 0
  );
};

const getAssignedBirdsFromHandlerEntries = (handler) => {
  const entries = Array.isArray(handler?.handlerInfo?.breeders)
    ? handler.handlerInfo.breeders
    : [];

  return entries.reduce((total, entry) => {
    const birds = firstDefined(entry?.birds, entry?.Birds);
    const birdsLength = Array.isArray(birds) ? birds.length : 0;

    const explicitCount = toNumber(
      firstDefined(
        entry?.numberOfBirds,
        entry?.NumberOfBirds,
        entry?.birdsRequested,
        entry?.BirdsRequested,
        entry?.birdCount,
        entry?.BirdCount,
        entry?.assignedBirds,
        entry?.AssignedBirds
      )
    );

    return total + Math.max(explicitCount, birdsLength);
  }, 0);
};

/**
 * Calculate the number of open slots for a handler based on approved requests
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests (approved/pending/cancelled)
 * @returns {number} Number of open slots available
 */
export const calculateHandlerOpenSlots = (handler, allRequests = []) => {
  if (!handler) {
    return 0;
  }

  const totalBirds = getHandlerTotalCapacity(handler);
  const targetHandlerId = normalizeId(handler?.id);

  // Count already-assigned requests for this handler
  const approvedRequests = uniqueRequests(allRequests).filter(request => 
    getRequestHandlerId(request) === targetHandlerId &&
    isAssignedRequest(request)
  );

  // Calculate total assigned birds
  const approvedBirds = approvedRequests.reduce((total, request) => {
    return total + getRequestBirdCount(request);
  }, 0);

  const handlerEntryBirds = getAssignedBirdsFromHandlerEntries(handler);
  const assignedBirds = Math.max(approvedBirds, handlerEntryBirds);

  // Open slots are strictly capacity - assigned birds (can be negative when over capacity)
  return totalBirds - assignedBirds;
};

/**
 * Calculate how many more birds a handler can accept from a specific breeder
 * Takes into account the 5-bird limit per handler per breeder
 * @param {Object} handler - Handler participant object
 * @param {string} breederId - ID of the breeder
 * @param {Array} allRequests - All handler requests
 * @returns {number} Number of birds this handler can still accept from this breeder
 */
export const calculateAvailableSlotsForBreeder = (handler, breederId, allRequests = []) => {
  const openSlots = calculateHandlerOpenSlots(handler, allRequests);
  
  if (openSlots <= 0) {
    return 0;
  }

  const targetHandlerId = normalizeId(handler?.id);
  const targetBreederId = normalizeId(breederId);

  // Count approved birds this handler already has from this breeder
  const approvedRequestsFromBreeder = uniqueRequests(allRequests).filter(request => 
    getRequestHandlerId(request) === targetHandlerId &&
    getRequestBreederId(request) === targetBreederId &&
    isAssignedRequest(request)
  );

  const currentBirdsFromBreeder = approvedRequestsFromBreeder.reduce((total, request) => {
    return total + getRequestBirdCount(request);
  }, 0);

  // Handler can accept max 5 birds per breeder
  const remainingBreederSlots = Math.max(0, 5 - currentBirdsFromBreeder);

  // Return the minimum of open slots and remaining breeder slots
  return Math.min(openSlots, remainingBreederSlots);
};

/**
 * Check if a handler is at full capacity
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests
 * @returns {boolean} True if handler is at capacity
 */
export const isHandlerAtCapacity = (handler, allRequests = []) => {
  return calculateHandlerOpenSlots(handler, allRequests) <= 0;
};

/**
 * Get handler capacity summary for display
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests
 * @returns {Object} Capacity summary with totalBirds, reserved, approved, openSlots
 */
export const getHandlerCapacitySummary = (handler, allRequests = []) => {
  if (!handler) {
    return {
      totalBirds: 0,
      reserved: 0,
      approved: 0,
      openSlots: 0,
      isAtCapacity: true
    };
  }

  const totalBirds = getHandlerTotalCapacity(handler);
  const reserved = getHandlerReservedSlots(handler);
  const openSlots = calculateHandlerOpenSlots(handler, allRequests);
  const approved = Math.max(0, totalBirds - openSlots);

  return {
    totalBirds,
    reserved,
    approved,
    openSlots,
    isAtCapacity: openSlots <= 0
  };
};